import { S3, S3ClientConfig } from "@aws-sdk/client-s3";
import { Upload } from "@aws-sdk/lib-storage";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { isPollStoppedSaga } from "modules/polling/sagas";
import { pendingPollsSelector } from "modules/polling/selectors";
import { EventChannel, SagaIterator, eventChannel } from "redux-saga";
import {
  all,
  call,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading
} from "redux-saga/effects";
import { downloadFile } from "utils/downloadFile";
import { getAxiosErrorMessage } from "utils/getAxiosErrorMessage";
import { appConfig } from "../../appConfig";
import { axiosInstance } from "../../axios";
import { MAX_FILE_SIZE } from "../../constants";
import { objectStorageActions } from "./slices";
import {
  ChangeBucketVisibilityParams,
  CheckCorsPolicyParams,
  CheckCorsPolicyResponse,
  CreateBucketParams,
  CreateBucketResponse,
  CreateCredentialsParams,
  CreateCredentialsResponse,
  CreateFolderParams,
  CreateSubUserKeyParams,
  CreateSubUserKeyResponse,
  CreateSubUserParams,
  CreateSubUserResponse,
  DeleteBucketLifecycleRuleParams,
  DeleteBucketParams,
  DeleteCredentialsParams,
  DeleteFileAllVersionsParams,
  DeleteFileParams,
  DeleteFilesParams,
  DeleteSubUserKeyParams,
  DeleteSubUserParams,
  DownloadFileParams,
  DownloadFileResponse,
  GetBucketAclParams,
  GetBucketAclResponse,
  GetBucketCorsPolicyParams,
  GetBucketCorsPolicyResponse,
  GetBucketLifecyclePolicyParams,
  GetBucketLifecyclePolicyResponse,
  GetBucketLifecycleRuleParams,
  GetBucketLifecycleRuleResponse,
  GetBucketPolicyParams,
  GetBucketPolicyResponse,
  GetBucketParams,
  GetBucketResponse,
  GetBucketsParams,
  GetBucketsResponse,
  GetCredentialsListParams,
  GetCredentialsListResponse,
  GetCredentialsParams,
  GetCredentialsResponse,
  GetFileAllVersionsParams,
  GetFileAllVersionsResponse,
  GetFileSignedDownloadLinkParams,
  GetFileSignedDownloadLinkResponse,
  GetFilesParams,
  GetFilesResponse,
  GetFlatFilesParams,
  GetSubUserKeyParams,
  GetSubUserKeyResponse,
  GetSubUsersParams,
  GetSubUsersResponse,
  RegenerateCredentialsParams,
  RegenerateCredentialsResponse,
  SetBucketLifecycleRuleParams,
  SetBucketLifecycleRuleResponse,
  SetCorsPolicyParams,
  UpdateVersioningStatusParams,
  UploadFileParams
} from "./types";

export function* getCredentialsSaga(
  action: PayloadAction<GetCredentialsParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId } = action.payload;
    const response: AxiosResponse<GetCredentialsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-identity/method/${projId}/creds/${credsId}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getCredentialsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getCredentialsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get credentials data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getCredentialsListSaga(
  action: PayloadAction<GetCredentialsListParams>
): SagaIterator<void> {
  try {
    const { regionId, projId } = action.payload;
    const response: AxiosResponse<GetCredentialsListResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-identity/method/${projId}/creds`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getCredentialsListSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getCredentialsListFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get credentials data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createCredentialsSaga(
  action: PayloadAction<CreateCredentialsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the master key...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId } = action.payload;
    const response: AxiosResponse<CreateCredentialsResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-identity/method/${projId}/creds`
    );
    yield put(objectStorageActions.createCredentialsSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Master key has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.createCredentialsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create master key",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* regenerateCredentialsSaga(
  action: PayloadAction<RegenerateCredentialsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Re-generating the master key secret...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId } = action.payload;
    const response: AxiosResponse<RegenerateCredentialsResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-identity/method/${projId}/creds/${credsId}`
    );
    yield put(objectStorageActions.regenerateCredentialsSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Master key secret has been successfully re-generated.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.regenerateCredentialsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to re-generate master key secret",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteCredentialsSaga(
  action: PayloadAction<DeleteCredentialsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the master key...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-identity/method/${projId}/creds/${credsId}`
    );
    yield put(objectStorageActions.deleteCredentialsSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Master key has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteCredentialsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete master key",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSubUsersSaga(
  action: PayloadAction<GetSubUsersParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId } = action.payload;
    const response: AxiosResponse<GetSubUsersResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getSubUsersSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getSubUsersFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get subusers data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createSubUserSaga(
  action: PayloadAction<CreateSubUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the subuser...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, data } = action.payload;
    const response: AxiosResponse<CreateSubUserResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers`,
      {
        subuser_id: data.name,
        access: data.access
      }
    );
    yield put(objectStorageActions.createSubUserSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Subuser has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.createSubUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create subuser",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteSubUserSaga(
  action: PayloadAction<DeleteSubUserParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the subuser...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, subUserName } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers/${subUserName}`
    );
    yield put(objectStorageActions.deleteSubUserSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Subuser has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteSubUserFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete subuser",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getSubUserKeySaga(
  action: PayloadAction<GetSubUserKeyParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, subUserName, accessKey } =
      action.payload;
    const response: AxiosResponse<GetSubUserKeyResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers/${subUserName}/keys/${accessKey}`
    );
    const key = Array.isArray(response.data)
      ? response.data.find((item) => item.access_key === accessKey)
      : response.data;
    if (!key) {
      throw new Error("The key is missing in the response.");
    }
    yield put(objectStorageActions.getSubUserKeySuccess(key));
  } catch (e) {
    yield put(
      objectStorageActions.getSubUserKeyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get the key data",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createSubUserKeySaga(
  action: PayloadAction<CreateSubUserKeyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the key...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, subUserName } = action.payload;
    const response: AxiosResponse<CreateSubUserKeyResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers/${subUserName}/keys`
    );
    yield put(objectStorageActions.createSubUserKeySuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Key has been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.createSubUserKeyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create key",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteSubUserKeySaga(
  action: PayloadAction<DeleteSubUserKeyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the key...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, subUserName, accessKey } =
      action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/subusers/${subUserName}/keys/${accessKey}`
    );
    yield put(objectStorageActions.deleteSubUserKeySuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Key has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteSubUserKeyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete key",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getBucketsListSaga(
  action: PayloadAction<GetBucketsParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId } = action.payload;
    const response: AxiosResponse<GetBucketsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getBucketsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getBucketsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get buckets list",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getBucketSaga(
  action: PayloadAction<GetBucketParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getBucketSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getBucketFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    const pendingPolls: string[] = yield select(pendingPollsSelector);
    if (pendingPolls.includes("S3_FILES/BUCKET")) {
      yield put(
        notificationsActions.showNotification({
          title: "Failed to get bucket",
          text: getAxiosErrorMessage(e),
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }
}

export function* createBucketSaga(
  action: PayloadAction<CreateBucketParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating the bucket...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, data } = action.payload;
    const response: AxiosResponse<CreateBucketResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/bucket/${data.name}`
    );
    yield put(objectStorageActions.createBucketSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "Bucket have been successfully created.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.createBucketFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create bucket",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* changeBucketVisibilitySaga(
  action: PayloadAction<ChangeBucketVisibilityParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Changing bucket visibility...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, visibility } =
      action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/visibility?visibility=${visibility}`
    );
    yield put(objectStorageActions.changeBucketVisibilitySuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Bucket visibility has been changed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.changeBucketVisibilityFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to change bucket visibility",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteBucketSaga(
  action: PayloadAction<DeleteBucketParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the bucket...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, name } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${name}`
    );
    yield put(objectStorageActions.deleteBucketSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Bucket has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteBucketFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete bucket",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

// NOTE: Dead code — this saga (force-delete a non-empty bucket via `/force`) is
// not registered in `watcherSaga`, not imported anywhere, and has no dedicated
// action, so it is unreachable. Kept as-is during the Redux Toolkit migration.
// To enable it: add an action + watcher entry + a UI trigger.
export function* deleteNonEmptyBucketSaga(
  action: PayloadAction<DeleteBucketParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the bucket...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, name } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${name}/force`
    );
    yield put(objectStorageActions.deleteBucketSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Bucket has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteBucketFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete bucket",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getBucketLifecycleRuleSaga(
  action: PayloadAction<GetBucketLifecycleRuleParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketLifecycleRuleResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/object-expiration`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        objectStorageActions.getBucketLifecycleRuleSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      objectStorageActions.getBucketLifecycleRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );

    yield put(
      notificationsActions.showNotification({
        title: "Failed to get bucket lifecycle rule",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* setBucketLifecycleRuleSaga(
  action: PayloadAction<SetBucketLifecycleRuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Setting the bucket lifecycle rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, data } = action.payload;
    const response: AxiosResponse<SetBucketLifecycleRuleResponse> = yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/object-expiration`,
      data
    );
    yield put(
      objectStorageActions.setBucketLifecycleRuleSuccess(response.data)
    );
    yield put(
      notificationsActions.showNotification({
        title: "Bucket lifecycle rule has been successfully set.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.setBucketLifecycleRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to set bucket lifecycle rule.",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteBucketLifecycleRuleSaga(
  action: PayloadAction<DeleteBucketLifecycleRuleParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the bucket lifecycle rule...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/object-expiration`
    );
    yield put(objectStorageActions.deleteBucketLifecycleRuleSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Bucket lifecycle rule has been successfully deleted.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteBucketLifecycleRuleFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete bucket lifecycle rule.",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

/*
  Bucket policies.

  Each policy has its own endpoint and each of them is optional: a bucket
  without a policy answers with an error, which is a normal state here and not
  something worth a notification, so the failures stay silent.
*/
export function* getBucketPolicySaga(
  action: PayloadAction<GetBucketPolicyParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketPolicyResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/policy`
    );
    yield put(objectStorageActions.getBucketPolicySuccess(response.data));
  } catch (e) {
    yield put(
      objectStorageActions.getBucketPolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* getBucketLifecyclePolicySaga(
  action: PayloadAction<GetBucketLifecyclePolicyParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketLifecyclePolicyResponse> =
      yield call(
        axiosInstance.get,
        `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/lifecycle`
      );
    yield put(
      objectStorageActions.getBucketLifecyclePolicySuccess(response.data)
    );
  } catch (e) {
    yield put(
      objectStorageActions.getBucketLifecyclePolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* getBucketCorsPolicySaga(
  action: PayloadAction<GetBucketCorsPolicyParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketCorsPolicyResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/cors-policy`
    );
    yield put(objectStorageActions.getBucketCorsPolicySuccess(response.data));
  } catch (e) {
    yield put(
      objectStorageActions.getBucketCorsPolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* getBucketAclSaga(
  action: PayloadAction<GetBucketAclParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<GetBucketAclResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/acl`
    );
    yield put(objectStorageActions.getBucketAclSuccess(response.data));
  } catch (e) {
    yield put(
      objectStorageActions.getBucketAclFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* getFilesListSaga(
  action: PayloadAction<GetFilesParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName, folderName, offset, limit } =
      action.payload;
    const response: AxiosResponse<GetFilesResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/folders?path=${
        folderName || ""
      }&offset=${offset}&limit=${limit}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getFilesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getFilesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    const pendingPolls: string[] = yield select(pendingPollsSelector);
    if (pendingPolls.includes("S3_FILES/BUCKET")) {
      yield put(
        notificationsActions.showNotification({
          title: "Failed to get objects list",
          text: getAxiosErrorMessage(e),
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }
}

export function* getFlatFilesListSaga(
  action: PayloadAction<GetFlatFilesParams>
): SagaIterator<void> {
  try {
    const {
      regionId,
      projId,
      credsId,
      bucketName,
      max_keys,
      page_map,
      page,
      is_flat_list,
      prefix,
      delimiter
    } = action.payload;
    const response: AxiosResponse<GetFilesResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/flat`,
      {
        max_keys,
        page_map,
        // delimiter: "/",
        page,
        is_flat_list,
        delimiter,
        prefix
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getFlatFilesSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getFlatFilesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    const pendingPolls: string[] = yield select(pendingPollsSelector);
    if (pendingPolls.includes("S3_FILES/BUCKET")) {
      yield put(
        notificationsActions.showNotification({
          title: "Failed to get flat objects list",
          text: getAxiosErrorMessage(e),
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }
}

export function* getFlatFilesListRefreshedSaga(
  action: PayloadAction<GetFlatFilesParams>
): SagaIterator<void> {
  try {
    const {
      regionId,
      projId,
      credsId,
      bucketName,
      max_keys,
      page_map,
      page,
      is_flat_list,
      prefix,
      delimiter
    } = action.payload;
    const response: AxiosResponse<GetFilesResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/flat`,
      {
        max_keys,
        page_map,
        page,
        is_flat_list,
        delimiter,
        prefix
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        objectStorageActions.getFlatFilesRefreshedSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      objectStorageActions.getFlatFilesRefreshedFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    const pendingPolls: string[] = yield select(pendingPollsSelector);
    if (pendingPolls.includes("S3_FILES/BUCKET")) {
      yield put(
        notificationsActions.showNotification({
          title: "Failed to get flat objects list after refresh",
          text: getAxiosErrorMessage(e),
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }
}

export function* getFlatFilesListByPrefixSaga(
  action: PayloadAction<GetFlatFilesParams>
): SagaIterator<void> {
  try {
    const {
      regionId,
      projId,
      credsId,
      bucketName,
      max_keys,
      page_map,
      page,
      is_flat_list,
      prefix,
      delimiter
    } = action.payload;
    const response: AxiosResponse<GetFilesResponse> = yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/flat`,
      {
        max_keys,
        page_map,
        page,
        is_flat_list,
        delimiter,
        prefix
      }
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(
        objectStorageActions.getFlatFilesByPrefixSuccess(response.data)
      );
    }
  } catch (e) {
    yield put(
      objectStorageActions.getFlatFilesByPrefixFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    const pendingPolls: string[] = yield select(pendingPollsSelector);
    if (pendingPolls.includes("S3_FILES/BUCKET")) {
      yield put(
        notificationsActions.showNotification({
          title: "Failed to get flat objects list by prefix",
          text: getAxiosErrorMessage(e),
          type: NOTIFICATION_TYPES.ERROR
        })
      );
    }
  }
}

export function* uploadFileSaga(
  action: PayloadAction<UploadFileParams>
): SagaIterator<void> {
  const {
    regionId,
    accessKey,
    accessSecret,
    endpoint,
    projId,
    bucketName,
    file,
    folder
  } = action.payload;

  try {
    const s3Config: S3ClientConfig = {
      endpoint: endpoint,
      region: regionId,
      credentials: {
        accessKeyId: accessKey,
        secretAccessKey: accessSecret
      }
    };

    const s3 = new S3(s3Config);

    if (file.size > MAX_FILE_SIZE) {
      yield put(
        objectStorageActions.uploadFileFailure({
          params: action.payload,
          error: "The file size is over 5 GiB."
        })
      );
      yield put(
        notificationsActions.showNotification({
          title: "Please, select a file smaller than 5 GiB.",
          type: NOTIFICATION_TYPES.ERROR
        })
      );
      return;
    }

    const params = {
      Bucket: `${projId}:${bucketName}`,
      Key: folder + file.name,
      Body: file
    };

    const parallelUploads3 = new Upload({
      client: s3,
      params: params
    });

    const progressChannel: EventChannel<number> = yield call(
      eventChannel,
      (emit) => {
        const uploadProgressHandler = (progress: {
          loaded?: number;
          total?: number;
        }) => {
          if (progress.loaded && progress.total) {
            const percentageProgress = Math.round(
              (progress.loaded / progress.total) * 100
            );
            emit(percentageProgress);
          }
        };

        parallelUploads3.on("httpUploadProgress", uploadProgressHandler);

        return () => {
          parallelUploads3.off("httpUploadProgress", uploadProgressHandler);
        };
      }
    );

    yield fork(function* () {
      while (true) {
        const progress = yield take(progressChannel);
        if (typeof progress === "number") {
          yield put(objectStorageActions.uploadProgress(progress));
        } else {
          break;
        }
      }
    });

    yield call([parallelUploads3, "done"]);
    progressChannel.close();

    yield put(
      notificationsActions.showNotification({
        title: "File has been successfully uploaded.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );

    yield put(
      objectStorageActions.uploadFileSuccess({
        message: "File uploaded successfully"
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.uploadFileFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );

    yield put(
      notificationsActions.showNotification({
        title: "Failed to upload file",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFileSaga(
  action: PayloadAction<DeleteFileParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the object...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, fileName } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/files?file=${fileName}`
    );
    yield put(objectStorageActions.deleteFileSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "The request has been successfully sent",
        text: "Object will be deleted in a few seconds.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteFileFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete object",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFilesSaga(
  action: PayloadAction<DeleteFilesParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the objects...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, objects } = action.payload;
    yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/files/delete`,
      { objects }
    );
    yield put(objectStorageActions.deleteFilesSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "The request has been successfully sent.",
        text: "Objects will be deleted in a few seconds.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteFilesFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete object",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFileSignedDownloadLinkSaga(
  action: PayloadAction<GetFileSignedDownloadLinkParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Generating the object downloaded link...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, fileName, duration } =
      action.payload;
    const response: AxiosResponse<GetFileSignedDownloadLinkResponse> =
      yield call(
        axiosInstance.get,
        `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/${fileName}/link?file=${fileName}`,
        {
          params: {
            duration
          }
        }
      );

    yield put(
      objectStorageActions.getFileSignedDownloadLinkSuccess(response.data)
    );
    yield put(
      notificationsActions.showNotification({
        title: `Open to see generated link...`,
        text: `${response.data}`,
        type: NOTIFICATION_TYPES.INFO
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.getFileSignedDownloadLinkFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to generate link",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* downloadFileSaga(
  action: PayloadAction<DownloadFileParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Downloading...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, fileName } = action.payload;
    const response: AxiosResponse<DownloadFileResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/link?file=${fileName}`
    );

    downloadFile(response.data);
    yield put(objectStorageActions.downloadFileSuccess(response.data));
    yield put(
      notificationsActions.showNotification({
        title: "The request to download file has been successfully sent.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.downloadFileFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to download file",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* checkCorsPolicySaga(
  action: PayloadAction<CheckCorsPolicyParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName } = action.payload;
    const response: AxiosResponse<CheckCorsPolicyResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/cors-policy?origin=${appConfig.consoleDomain}`
    );
    yield put(objectStorageActions.checkCorsPolicySuccess(response.data));
  } catch (e) {
    yield put(
      objectStorageActions.checkCorsPolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
  }
}

export function* setCorsPolicySaga(
  action: PayloadAction<SetCorsPolicyParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Setting the Cors policy..",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName } = action.payload;
    yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/cors-policy?origin=${appConfig.consoleDomain}`
    );
    yield put(objectStorageActions.setCorsPolicySuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Access to the bucket has been allowed.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.setCorsPolicyFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to open access to the bucket.",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* createFolderSaga(
  action: PayloadAction<CreateFolderParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Creating folder...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, folderName } =
      action.payload;
    yield call(
      axiosInstance.post,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/folders?folder=${folderName}`
    );
    yield put(objectStorageActions.createFolderSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "The folder has been created",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.createFolderFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to create a folder",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* updateVersioningStatusSaga(
  action: PayloadAction<UpdateVersioningStatusParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Updating versioning status...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, versioningStatus } =
      action.payload;
    yield call(
      axiosInstance.put,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/versioning?status=${versioningStatus}`
    );
    yield put(objectStorageActions.updateVersioningStatusSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "Versioning status has been updated",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.updateVersioningStatusFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to update versioning status.",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* getFileAllVersionsSaga(
  action: PayloadAction<GetFileAllVersionsParams>
): SagaIterator<void> {
  try {
    const { regionId, projId, credsId, bucketName, fileName } = action.payload;
    const response: AxiosResponse<GetFileAllVersionsResponse> = yield call(
      axiosInstance.get,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/versions?key=${fileName}`
    );
    const isPollStopped: boolean = yield call(isPollStoppedSaga, action);
    if (!isPollStopped) {
      yield put(objectStorageActions.getFileAllVersionsSuccess(response.data));
    }
  } catch (e) {
    yield put(
      objectStorageActions.getFileAllVersionsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to get object's all versions",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* deleteFileAllVersionsSaga(
  action: PayloadAction<DeleteFileAllVersionsParams>
): SagaIterator<void> {
  try {
    yield put(
      notificationsActions.showNotification({
        title: "Deleting the object's all versions...",
        type: NOTIFICATION_TYPES.PROGRESS
      })
    );
    const { regionId, projId, credsId, bucketName, data } = action.payload;
    yield call(
      axiosInstance.delete,
      `gotham-${regionId}-object-storage/method/${projId}/credentials/${credsId}/buckets/${bucketName}/files/all-versions`,
      { data }
    );
    yield put(objectStorageActions.deleteFileAllVersionsSuccess());
    yield put(
      notificationsActions.showNotification({
        title: "The request has been successfully sent",
        text: "Object's all versions will be deleted in a few seconds.",
        type: NOTIFICATION_TYPES.SUCCESS
      })
    );
  } catch (e) {
    yield put(
      objectStorageActions.deleteFileAllVersionsFailure({
        params: action.payload,
        error: getAxiosErrorMessage(e)
      })
    );
    yield put(
      notificationsActions.showNotification({
        title: "Failed to delete object's all versions",
        text: getAxiosErrorMessage(e),
        type: NOTIFICATION_TYPES.ERROR
      })
    );
  }
}

export function* watcherSaga(): SagaIterator<void> {
  yield all([
    takeLeading(objectStorageActions.getCredentialsRequest, getCredentialsSaga),
    takeLeading(
      objectStorageActions.getCredentialsListRequest,
      getCredentialsListSaga
    ),
    takeLeading(objectStorageActions.getSubUsersRequest, getSubUsersSaga),
    takeLeading(objectStorageActions.getSubUserKeyRequest, getSubUserKeySaga),
    takeLeading(objectStorageActions.getBucketsRequest, getBucketsListSaga),
    takeLeading(objectStorageActions.getBucketRequest, getBucketSaga),
    takeLeading(
      objectStorageActions.getBucketLifecycleRuleRequest,
      getBucketLifecycleRuleSaga
    ),
    takeEvery(
      objectStorageActions.setBucketLifecycleRuleRequest,
      setBucketLifecycleRuleSaga
    ),
    takeEvery(
      objectStorageActions.deleteBucketLifecycleRuleRequest,
      deleteBucketLifecycleRuleSaga
    ),
    takeLeading(
      objectStorageActions.getBucketPolicyRequest,
      getBucketPolicySaga
    ),
    takeLeading(
      objectStorageActions.getBucketLifecyclePolicyRequest,
      getBucketLifecyclePolicySaga
    ),
    takeLeading(
      objectStorageActions.getBucketCorsPolicyRequest,
      getBucketCorsPolicySaga
    ),
    takeLeading(objectStorageActions.getBucketAclRequest, getBucketAclSaga),

    takeLatest(objectStorageActions.getFilesRequest, getFilesListSaga),
    takeLatest(objectStorageActions.getFlatFilesRequest, getFlatFilesListSaga),
    takeEvery(
      objectStorageActions.getFlatFilesByPrefixRequest,
      getFlatFilesListByPrefixSaga
    ),
    takeEvery(
      objectStorageActions.getFlatFilesRefreshedRequest,
      getFlatFilesListRefreshedSaga
    ),
    takeEvery(objectStorageActions.checkCorsPolicyRequest, checkCorsPolicySaga),
    takeEvery(objectStorageActions.setCorsPolicyRequest, setCorsPolicySaga),
    takeEvery(
      objectStorageActions.createCredentialsRequest,
      createCredentialsSaga
    ),
    takeEvery(
      objectStorageActions.regenerateCredentialsRequest,
      regenerateCredentialsSaga
    ),
    takeEvery(
      objectStorageActions.deleteCredentialsRequest,
      deleteCredentialsSaga
    ),
    takeEvery(objectStorageActions.createSubUserRequest, createSubUserSaga),
    takeEvery(objectStorageActions.deleteSubUserRequest, deleteSubUserSaga),
    takeEvery(
      objectStorageActions.createSubUserKeyRequest,
      createSubUserKeySaga
    ),
    takeEvery(
      objectStorageActions.deleteSubUserKeyRequest,
      deleteSubUserKeySaga
    ),
    takeEvery(objectStorageActions.createBucketRequest, createBucketSaga),
    takeEvery(
      objectStorageActions.changeBucketVisibilityRequest,
      changeBucketVisibilitySaga
    ),
    takeEvery(objectStorageActions.deleteBucketRequest, deleteBucketSaga),
    takeEvery(objectStorageActions.uploadFileRequest, uploadFileSaga),
    takeEvery(objectStorageActions.deleteFileRequest, deleteFileSaga),
    takeEvery(objectStorageActions.deleteFilesRequest, deleteFilesSaga),
    takeEvery(objectStorageActions.downloadFileRequest, downloadFileSaga),
    takeEvery(objectStorageActions.createFolderRequest, createFolderSaga),
    takeEvery(
      objectStorageActions.getFileSignedDownloadLinkRequest,
      getFileSignedDownloadLinkSaga
    ),
    takeEvery(
      objectStorageActions.updateVersioningStatusRequest,
      updateVersioningStatusSaga
    ),
    takeEvery(
      objectStorageActions.getFileAllVersionsRequest,
      getFileAllVersionsSaga
    ),
    takeEvery(
      objectStorageActions.deleteFileAllVersionsRequest,
      deleteFileAllVersionsSaga
    )
  ]);
}
